import { FACTORY_ADDRESS, INIT_CODE_HASH, WNATIVE, WNATIVE_ADDRESS } from '@emberswap/emberswap-sdk2/dist/constants'
import {
  BridgeUnlimited,
  ConstantProductRPool,
  LiquidityProviders,
  Native,
  // Pair,
  PoolCode,
  RToken,
  Token,
} from '@emberswap/emberswap-sdk2/dist/entities'
import { Type as Currency } from '@emberswap/emberswap-sdk2/dist/types'
import { getCreate2Address } from '@ethersproject/address'
import { BigNumber } from '@ethersproject/bignumber'
import { keccak256, pack } from '@ethersproject/solidity'
import { useMemo } from 'react'
import { Address, useContractReads } from 'wagmi'

import { UniswapV2BaseProvider } from '../classes/liquidity-providers/UniswapV2Base'
import { ConstantProductPoolCode } from '../classes/pools/ConstantProductPool'
import { NativeWrapBridgePoolCode } from '../classes/pools/NativeWrapBridge'
import { emberPair } from '../constants/abis/emberPair'


/**
 * Computes a pair address
 * @param {string}factoryAddress The Uniswap V3 factory address
 * @param {Token}tokenA The first token of the pair, irrespective of sort order
 * @param {Token}tokenB The second token of the pair, irrespective of sort order
 * @param {string}initCodeHashManualOverride Override the init code hash used to compute the pool address if necessary
 * @return {string} The pair address
 */
export const computePairAddress = ({
  tokenA,
  tokenB,
  factoryAddress,
  initCodeHashManualOverride,
}: {
  tokenA: Token
  tokenB: Token,
  factoryAddress?: string
  initCodeHashManualOverride?: string
}): string => {
  const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA] // does safety checks
  return getCreate2Address(
    factoryAddress ?? FACTORY_ADDRESS[token0.chainId],
    keccak256(['bytes'], [pack(['address', 'address'], [token0.address, token1.address])]),
    initCodeHashManualOverride ?? INIT_CODE_HASH[token0.chainId]
  )
}
type UseContractReadsConfig = Parameters<typeof useContractReads>['0']

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID,
}
interface _contracts {
  chainId: number | undefined
  address: Address
  abi: typeof emberPair
  functionName: 'getReserves'
}
/**
 * @param {number | undefined}chainId
 * @param {[Currency, Currency][]}currencies
 * @param {UniswapV2BaseProvider[]} liquidityProviders
 * @return {[Token[], Token[], Record<string | number, _contracts>[]]}
 */
export function getPairs(
  chainId: number | undefined,
  currencies: [Currency | undefined, Currency | undefined][],
  liquidityProviders: UniswapV2BaseProvider[]
) {
  const filtered = currencies.filter((currencies): currencies is [Currency, Currency] => {
    const [currencyA, currencyB] = currencies
    return Boolean(
      currencyA &&
        currencyB &&
        currencyA.chainId === currencyB.chainId &&
        !currencyA.wrapped.equals(currencyB.wrapped) &&
        FACTORY_ADDRESS[currencyA.chainId]
    )
  })

  const [tokensA, tokensB] = filtered.reduce<[Token[], Token[]]>(
    (acc, [currencyA, currencyB]) => {
      acc[0].push(currencyA.wrapped)
      acc[1].push(currencyB.wrapped)

      return acc
    },
    [[], []]
  )

  const contracts: Map<string, Array<_contracts>> = new Map()
  for (let i = 0; i < liquidityProviders.length; i++) {
    contracts.set(
      liquidityProviders[i].factory,
      filtered.map(([currencyA, currencyB]) => ({
        chainId,
        address: computePairAddress({
          tokenA: currencyA.wrapped,
          tokenB: currencyB.wrapped,
          factoryAddress: liquidityProviders[i].factory,
          initCodeHashManualOverride: liquidityProviders[i].initCodeHash,
        }) as Address,
        abi: emberPair,
        functionName: 'getReserves' as const,
      }))
    )
  }

  return [tokensA, tokensB, contracts] as const
}

interface UsePairsReturn {
  isLoading: boolean
  isError: boolean
  data: Map<string, PoolCode> | undefined
}

/**
 * @param {number | undefined}chainId
 * @param {[Currency, Currency][]}currencies
 * @param {UniswapV2BaseProvider[]}   liquidityProviders
 * @param {Omit<NonNullable<UseContractReadsConfig>, 'contracts'>}config
 * @return {UsePairsReturn}
 */
export function usePairs(
  chainId: number | undefined,
  currencies: [Currency | undefined, Currency | undefined][],
  liquidityProviders: UniswapV2BaseProvider[],
  config?: Omit<NonNullable<UseContractReadsConfig>, 'contracts'>
): UsePairsReturn {
  const [tokensA, tokensB, contracts] = useMemo(
    () => getPairs(chainId, currencies, liquidityProviders),
    [chainId, currencies, liquidityProviders]
  )
  const _contracts = useMemo(() => Array.from(contracts.values()).flat(), [contracts])
  const { data, isLoading, isError } = useContractReads({
    contracts: _contracts,
    enabled:
      config?.enabled !== undefined
        ? config.enabled && _contracts.length > 0
        : _contracts.length > 0,
    watch: !(typeof config?.enabled !== undefined && !config?.enabled),
  })

  return useMemo(() => {
      const tMap: Map<string, PoolCode> = new Map()
      const native = Native.onChain(chainId ? chainId : 10000)
      const nativeRToken: RToken = {
        address: '',
        name: native.name,
        symbol: native.symbol,
        chainId: chainId,
        decimals: 18,
      }
      const bridge = new BridgeUnlimited(
        WNATIVE_ADDRESS[chainId ? chainId : 10000],
        nativeRToken,
        WNATIVE[chainId ? chainId : 10000] as RToken,
        0,
        50_000
      )
      tMap.set(bridge.address, new NativeWrapBridgePoolCode(bridge, LiquidityProviders.NativeWrap))

    let z = 0

    if (_contracts.length === 0 || !chainId) return { isLoading, isError, data: undefined }
    if (!data)
      return {
        isLoading,
        isError,
        data: undefined,
      }
    let index = 0

      data.map((result, i) => {
        if (index >= tokensA.length) {
          z++
          index = 0
        }
        const tokenA = tokensA[index]
        const tokenB = tokensB[index]
        index++
        if (!tokenA || !tokenB || tokenA.equals(tokenB)) return undefined
        if (!result) return undefined
        const reserve0 = result?.result?.[0]
        const reserve1 = result?.result?.[1]
        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
        const rPool = new ConstantProductRPool(
          _contracts[i].address,
          token0 as RToken,
          token1 as RToken,
          liquidityProviders[z].fee,
          reserve0 ? BigNumber.from(reserve0) : BigNumber.from(0),
          reserve1 ? BigNumber.from(reserve1) : BigNumber.from(0)
        )
        const pc = new ConstantProductPoolCode(
          rPool,
          liquidityProviders[z].getType(),
          liquidityProviders[z].getPoolProviderName(),
          liquidityProviders[z].getPoolProviderURI()
        )

        tMap.set(_contracts[i].address, pc)

        return tMap
      })
    return {
      isLoading,
      isError,
      data: tMap
    }
  }, [_contracts, chainId, data, isError, isLoading, liquidityProviders, tokensA, tokensB])
}

// interface UsePairReturn {
//   isLoading: boolean
//   isError: boolean
//   data: [PairState, Pair | null]
// }

/**
 * @param {number}chainId
 * @param {Exchange[]} exchanges
 * @param {Currency}tokenA
 * @param {Currency}tokenB
 * @param {Omit<UseContractReadsConfig, 'contracts'>}config
 * @return {UsePairReturn}
 */
// export function usePair(
//   chainId: number,
//   exchanges: Exchange[],
//   tokenA?: Currency,
//   tokenB?: Currency,
//   config?: Omit<UseContractReadsConfig, 'contracts'>
// ): UsePairReturn {
//   const inputs: [[Currency | undefined, Currency | undefined]] = useMemo(
//     () => [[tokenA, tokenB]],
//     [tokenA, tokenB]
//   )
//   const { data, isLoading, isError } = usePairs(chainId, inputs, exchanges, config)
// 
//   return useMemo(
//     () => ({
//       isLoading,
//       isError,
//       data: data?.[0],
//     }),
//     [data, isError, isLoading]
//   )
// }
